import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs } from 'swiper/modules';
import {
    FaShoppingCart, FaMapMarkerAlt,
    FaTruck, FaCheckCircle, FaTimesCircle
} from "react-icons/fa";
import "swiper/css";
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import "swiper/css/thumbs";
import Footer from './component/Footer';
import { useCart } from "./context/CartContext";
import { checkPinCode, proCodeGet, refrenceProducts } from './services/userProfile';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useWishlist } from './context/WishlistContext';
import api from './api';

const useWindowWidth = () => {
    const [width, setWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1200
    );
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return width;
};

const ProductDetails = () => {

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 576;
    const isTablet = windowWidth >= 576 && windowWidth < 992;
    const isDesktop = windowWidth >= 992;

    const [wishlistedProducts, setWishlistedProducts] = useState({});
    const { wishlistCount, setWishlistCount } = useWishlist();
    const [wishlist, setWishlist] = useState([]);

    const mainSwiperRef = useRef(null);
    const thumbsSwiperRef = useRef(null);
    const imgRef = useRef();

    const [zoomVisible, setZoomVisible] = useState(false);
    const [zoomLensPos, setZoomLensPos] = useState({ x: 0, y: 0 });
    const [backgroundPos, setBackgroundPos] = useState("0% 0%");
    const [activeIndex, setActiveIndex] = useState(0);
    const zoomScale = 2;

    const [isVisible, setIsVisible] = useState(false);
    const [activeTab, setActiveTab] = useState("description");
    const [quantity, setQuantity] = useState(1);
    const [pincode, setPincode] = useState("");
    const [message, setMessage] = useState("");
    const [copied, setCopied] = useState(false);
    const [refference, setRefference] = useState([]);
    const [coupon, setCoupon] = useState([]);
    const [availableCoupon, setAvailableCoupon] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { addToCart, cart, updateQuantity } = useCart();

    const product = location.state?.items;

    const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
    const [selectedSize, setSelectedSize] = useState('');
    const [mainImage, setMainImage] = useState('');

    const selectedVariant = product?.variants?.[selectedVariantIndex] || product?.variants?.[0];
    const selectedColor = selectedVariant?.color?.name || '';

    const cartItem = cart.find(
        it => it._id === product?._id &&
            it.variant?.color?.name === selectedColor &&
            it.variant?.size === selectedSize
    );
    const cartItemQty = cartItem?.quantity || 0;
    const isInCart = cartItemQty > 0;

    const selectedSizeObj = selectedVariant?.sizes?.find(s => s.size === selectedSize);
    const displayedPrice = selectedSizeObj?.discountedPrice ?? selectedVariant?.sizes?.[0]?.discountedPrice ?? 0;
    const originalPrice = selectedSizeObj?.originalPrice ?? selectedVariant?.sizes?.[0]?.originalPrice ?? 0;
    const selectedStock = selectedSizeObj?.stock ?? 0;

    const imagesToShow = selectedVariant?.images?.length > 0
        ? selectedVariant.images
        : ["https://via.placeholder.com/600x600?text=No+Image"];

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [location.key]);

    useEffect(() => {
        if (product?.variants?.length > 0) {
            const firstVariant = product.variants[0];
            setSelectedVariantIndex(0);
            setMainImage(firstVariant.images?.[0] || '');
            const firstInStock = firstVariant.sizes?.find(s => s.stock > 0);
            setSelectedSize(firstInStock?.size || firstVariant.sizes?.[0]?.size || '');
        }
    }, [product]);

    const handleColorChange = (variantIndex) => {
        const variant = product.variants[variantIndex];
        setSelectedVariantIndex(variantIndex);
        setMainImage(variant.images?.[0] || '');
        setActiveIndex(0);
        setQuantity(1);
        const firstInStock = variant.sizes?.find(s => s.stock > 0);
        setSelectedSize(firstInStock?.size || variant.sizes?.[0]?.size || '');
    };

    const handleSizeChange = (size) => {
        setSelectedSize(size);
        setQuantity(1);
        setMessage('');
    };

    const handleMouseMove = (e) => {
        if (!isDesktop) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const lensW = 300;
        const lensH = 300;
        const lensX = Math.max(0, Math.min(mouseX - lensW / 2, rect.width - lensW));
        const lensY = Math.max(0, Math.min(mouseY - lensH / 2, rect.height - lensH));
        setZoomLensPos({ x: lensX, y: lensY });
        const bgX = (lensX / (rect.width - lensW)) * 100;
        const bgY = (lensY / (rect.height - lensH)) * 100;
        setBackgroundPos(`${bgX}% ${bgY}%`);
    };

    const arrowStyle = (side) => ({
        position: "absolute", top: "50%", [side]: isMobile ? "6px" : "10px",
        transform: "translateY(-50%)", cursor: "pointer",
        fontSize: isMobile ? "16px" : "22px",
        background: "#CEB256", color: "#fff", borderRadius: "50%",
        width: isMobile ? "28px" : "40px",
        height: isMobile ? "28px" : "40px",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20,
        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        userSelect: "none",
    });

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    useEffect(() => {
        const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const refDetailProducts = async (_id) => {
        try {
            const res = await refrenceProducts(_id);
            setRefference(res.data.products);
        } catch (error) {
            console.log("error", error.message);
        }
    };

    useEffect(() => {
        if (product?._id) refDetailProducts(product._id);
    }, [product?._id]);

    const getNearestCoupon = (coupons, amount) => {
        if (!coupons || coupons.length === 0) return null;
        coupons.sort((a, b) => Math.abs(a.minOrderAmount - amount) - Math.abs(b.minOrderAmount - amount));
        return coupons[0];
    };

    const extraDiscount = async () => {
        try {
            const res = await proCodeGet();
            const activeCoupon = getNearestCoupon(res.data.data, displayedPrice);
            setAvailableCoupon(activeCoupon);
            setCoupon(res.data.data);
        } catch (error) {
            console.log("error", error.message);
        }
    };
    useEffect(() => { extraDiscount(); }, []);

    const handleCopy = () => {
        if (availableCoupon?.code) {
            navigator.clipboard.writeText(availableCoupon.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const pincodeCheck = () => {
        const run = async () => {
            if (!pincode || pincode.trim() === "") { setMessage("Please provide a valid 6-digit code."); return; }
            if (!/^[0-9]{6}$/.test(pincode)) { setMessage("Please enter a valid 6-digit pincode."); return; }
            try {
                const res = await checkPinCode(pincode);
                if (res?.data?.success) {
                    const today = new Date();
                    today.setDate(today.getDate() + 6);
                    setMessage(`Expected delivery by ${today.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long" })}`);
                } else {
                    setMessage("Sorry, we're unable to deliver to this location.");
                }
            } catch {
                setMessage("Sorry, we're unable to deliver to this location.");
            }
        };
        run();
    };

    const handleBuyNow = (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
        if (!selectedVariant) { console.error("Selected variant not found!"); return; }
        const updatedProduct = { ...product, variant: selectedVariant, quantity };
        if (!token) { navigate("/user-login"); return; }
        navigate("/check-out", {
            state: { token, products: updatedProduct, singleProduct: "yes", singleProductId: product._id },
        });
    };

    const getSelectedVariant = (product, selectedColor, selectedSize) => {
        if (!product?.variants) return null;
        const colorVariant = product.variants.find(v => v.color.name === selectedColor);
        if (!colorVariant) return null;
        const sizeVariant = colorVariant.sizes.find(s => s.size === selectedSize);
        if (!sizeVariant) return null;
        return {
            color: colorVariant.color,
            size: sizeVariant.size,
            originalPrice: sizeVariant.originalPrice,
            discountedPrice: sizeVariant.discountedPrice,
            stock: sizeVariant.stock,
            images: colorVariant.images,
        };
    };

    const goToSingle = (product) => {
        navigate(`/product-details/${product._id}`, { state: { items: product } });
    };

    const addWishList = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }
        const isWishlisted = !!wishlistedProducts[product._id];
        try {
            await api.put("/user/add-wishlist", { productId: product._id, product }, { headers: { Authorization: `Bearer ${token}` } });
            setWishlistedProducts(prev => ({ ...prev, [product._id]: !isWishlisted }));
            setWishlistCount(prev => !isWishlisted ? prev + 1 : Math.max(prev - 1, 0));
            if (isWishlisted) setWishlist(prev => prev.filter(item => item._id !== product._id));
            else setWishlist(prev => [...prev, product]);
        } catch (err) {
            console.error("Error updating wishlist:", err);
            alert("Failed to update wishlist.");
        }
    };

    if (!product) return (
        <div style={{ padding: 40, textAlign: 'center' }}>
            Product not found. <Link to="/">Go Home</Link>
        </div>
    );

    const mainImgHeight = isMobile ? "auto" : isTablet ? "420px" : "600px";
    const thumbImgHeight = isMobile ? "56px" : "120px";
    const thumbColumnHeight = isTablet ? "420px" : "600px";

    return (
        <>
            <style>{`
                *, *::before, *::after { box-sizing: border-box; }

                .pd-gallery-wrap {
                    display: flex;
                    flex-direction: row;
                    gap: 12px;
                    align-items: flex-start;
                    width: 100%;
                }

                .pd-main-image-outer {
                    position: relative;
                    border-radius: 10px;
                    overflow: hidden;
                    flex: 1;
                    min-width: 0;
                }
                .pd-main-image-swiper {
                    width: 100%;
                    height: 600px;
                    border-radius: 10px;
                }
                .pd-main-image-swiper .swiper-slide {
                    overflow: hidden;
                }
                .pd-main-image-swiper img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 10px;
                    display: block;
                }

                .pd-thumb-vertical {
                    display: block;
                    width: 90px;
                    flex-shrink: 0;
                }
                .pd-thumb-vertical .swiper-slide {
                    height: auto !important;
                }

                .pd-thumb-horizontal {
                    display: none;
                    overflow-x: auto;
                    gap: 8px;
                    padding: 8px 0 4px;
                    margin-top: 8px;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                }
                .pd-thumb-horizontal::-webkit-scrollbar { display: none; }
                .pd-thumb-horizontal img {
                    flex-shrink: 0;
                    width: 60px;
                    height: 60px;
                    object-fit: cover;
                    object-position: center top;
                    border-radius: 6px;
                    cursor: pointer;
                    border: 1px solid #ddd;
                    transition: border 0.2s;
                }
                .pd-thumb-horizontal img.active-thumb {
                    border: 2px solid #CEB256;
                }

                .pd-zoom-preview {
                    position: absolute;
                    top: 0;
                    left: calc(100% + 16px);
                    width: 420px;
                    height: 100%;
                    border: 1px solid #ccc;
                    background-repeat: no-repeat;
                    pointer-events: none;
                    z-index: 9999;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.18);
                    border-radius: 8px;
                }

                .pd-action-row {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .quantity__box {
                    display: flex;
                    align-items: center;
                    border: 2px solid #CEB256;
                    border-radius: 6px;
                    overflow: hidden;
                    height: 42px;
                    flex-shrink: 0;
                }
                .quantity__box button {
                    width: 36px;
                    height: 100%;
                    background: #CEB256;
                    color: #fff;
                    border: none;
                    font-size: 18px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .quantity__box input {
                    width: 44px;
                    text-align: center;
                    border: none;
                    outline: none;
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;
                    padding: 0;
                }
                .quantity__box input[type=number]::-webkit-inner-spin-button,
                .quantity__box input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
                .quantity__box input[type=number] { -moz-appearance: textfield; }

                .pd-cart-stepper {
                    display: flex;
                    align-items: center;
                    border: 2px solid #CEB256;
                    border-radius: 6px;
                    overflow: hidden;
                    height: 42px;
                    flex-shrink: 0;
                }
                .pd-cart-stepper button {
                    width: 38px;
                    height: 100%;
                    background: #CEB256;
                    color: #fff;
                    border: none;
                    font-size: 20px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .pd-cart-stepper span {
                    min-width: 44px;
                    text-align: center;
                    font-weight: 700;
                    font-size: 16px;
                    color: #333;
                    padding: 0 6px;
                }

                .pd-addcart-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 10px 18px;
                    background: #CEB256;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    white-space: nowrap;
                    height: 42px;
                    flex-shrink: 0;
                }
                .pd-addcart-btn:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                .pd-buynow-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 10px 18px;
                    background: #28a745;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    height: 42px;
                    flex-shrink: 0;
                }
                .pd-buynow-btn:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                .pd-coupon-box {
                    padding: 10px 12px;
                    border: 1px solid #CEB256;
                    border-radius: 6px;
                    margin: 10px 0;
                    background: #fffdf3;
                    width: 100%;
                    max-width: 420px;
                }

                .pd-pincode-wrap {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    border: 2px solid #CEB256;
                    border-radius: 5px;
                    padding: 4px 8px;
                    background: #fff;
                    margin-top: 6px;
                    width: 100%;
                    max-width: 420px;
                }
                .pd-pincode-wrap input {
                    flex: 1;
                    border: none;
                    outline: none;
                    font-size: 14px;
                    padding: 6px 4px;
                    min-width: 0;
                }
                .pd-pincode-wrap button {
                    background: #CEB256;
                    border: none;
                    border-radius: 4px;
                    color: #fff;
                    padding: 7px 14px;
                    cursor: pointer;
                    font-weight: 600;
                    font-size: 14px;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .product__details--tab {
                    list-style: none;
                    padding: 0;
                    margin: 0 0 20px;
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                    border-bottom: 2px solid #eee;
                    padding-bottom: 4px;
                }
                .product__details--tab__list {
                    padding: 8px 16px;
                    cursor: pointer;
                    font-weight: 600;
                    border-radius: 4px 4px 0 0;
                    font-size: 14px;
                    color: #555;
                    border: 1px solid transparent;
                    transition: all 0.2s;
                    white-space: nowrap;
                }
                .product__details--tab__list.active {
                    background: #CEB256;
                    color: #fff;
                    border-color: #CEB256;
                }
                .product__details--tab__list:hover:not(.active) {
                    background: #faf3df;
                    color: #CEB256;
                }

                .pd-ref-card {
                    width: 100%;
                    border: 1px solid #eee;
                    border-radius: 8px;
                    padding: 10px;
                    background: #fff;
                    text-align: center;
                    transition: box-shadow 0.2s;
                }
                .pd-ref-card:hover {
                    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
                }

                @media (min-width: 576px) and (max-width: 991px) {
                    .pd-thumb-vertical {
                        width: 72px !important;
                    }
                    .pd-main-image-swiper {
                        height: 420px !important;
                    }
                    .pd-zoom-preview {
                        display: none !important;
                    }
                    .pd-action-row {
                        flex-wrap: wrap;
                    }
                    .pd-buynow-btn,
                    .pd-addcart-btn {
                        flex: 1;
                        min-width: 120px;
                    }
                }

                @media (max-width: 575px) {
                    .breadcrumb__section {
                        padding: 20px 0;
                    }
                    .pd-gallery-wrap {
                        flex-direction: row !important;
                        gap: 0 !important;
                    }
                    .pd-thumb-vertical {
                        display: none !important;
                    }
                    .pd-thumb-horizontal {
                        display: flex !important;
                    }
                    .pd-main-image-outer {
                        width: 100% !important;
                        height: auto !important;
                    }
                    .pd-main-image-swiper {
                        height: auto !important;
                        aspect-ratio: 4 / 5;
                    }
                    .pd-main-image-swiper .swiper-wrapper,
                    .pd-main-image-swiper .swiper-slide,
                    .pd-main-image-swiper .swiper-slide > div {
                        height: 100% !important;
                    }
                    .pd-main-image-swiper img {
                        object-fit: cover;
                        object-position: center top;
                    }
                    .pd-zoom-preview {
                        display: none !important;
                    }

                    .pd-action-row {
                        flex-direction: row;
                        align-items: normal;
                        gap: 10px !important;
                        width: 160px;
                        flex-wrap: nowrap;
                    }
                    .quantity__box {
                        width: 100%;
                        justify-content: center;
                    }
                    .quantity__box input {
                        flex: 1;
                        width: inherit;
                    }
                    .pd-cart-stepper {
                        width: 100%;
                        justify-content: center;
                    }
                    .pd-cart-stepper span {
                        flex: 1;
                    }
                    .pd-addcart-btn {
                        width: 100% !important;
                        height: 46px !important;
                        font-size: 15px !important;
                    }
                    .pd-buynow-btn {
                        width: 100% !important;
                        height: 46px !important;
                        font-size: 15px !important;
                    }

                    .pd-coupon-box {
                        max-width: 100% !important;
                    }

                    .pd-pincode-wrap {
                        max-width: 100% !important;
                        flex-wrap: wrap;
                    }
                    .pd-pincode-wrap input {
                        width: 100%;
                        min-width: 0;
                    }
                    .pd-pincode-wrap button {
                        width: 100%;
                    }

                    .product__details--tab__list {
                        font-size: 12px !important;
                        padding: 6px 10px !important;
                    }

                    .section--padding {
                        padding-top: 24px !important;
                        padding-bottom: 24px !important;
                    }
                    .product__details--section .container {
                        padding-left: 12px;
                        padding-right: 12px;
                    }
                }
            `}</style>

            <ToastContainer />

            <main className="main__content_wrapper">

                <section className="product__details--section section--padding">
                    <div className="container">
                        <div className="row row-cols-lg-2 row-cols-md-2 row-cols-1">

                            <div className="col">
                                <div className="product__details--media">

                                    <div className="pd-gallery-wrap">

                                        {!isMobile && (
                                            <div
                                                className="pd-thumb-vertical"
                                                style={{
                                                    position: "relative",
                                                    width: isTablet ? "72px" : "90px",
                                                    height: thumbColumnHeight,
                                                    borderRadius: "10px",
                                                    overflow: "hidden",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Swiper
                                                    direction="vertical"
                                                    slidesPerView={isTablet ? 3 : 4}
                                                    spaceBetween={8}
                                                    navigation={{ nextEl: ".thumb-next", prevEl: ".thumb-prev" }}
                                                    modules={[Navigation, Thumbs]}
                                                    onSwiper={(swiper) => { thumbsSwiperRef.current = swiper; }}
                                                    style={{ height: "calc(100% - 64px)", marginTop: "32px", marginBottom: "32px" }}
                                                >
                                                    {imagesToShow.map((img, i) => (
                                                        <SwiperSlide key={i}>
                                                            <img
                                                                src={img}
                                                                alt={`Thumbnail ${i + 1}`}
                                                                style={{
                                                                    width: "100%",
                                                                    height: thumbImgHeight,
                                                                    objectFit: "cover",
                                                                    borderRadius: "6px",
                                                                    cursor: "pointer",
                                                                    border: i === activeIndex ? "2px solid #CEB256" : "1px solid #ddd",
                                                                    transition: "border 0.2s",
                                                                    display: "block",
                                                                }}
                                                                onMouseEnter={() => {
                                                                    setMainImage(img);
                                                                    setActiveIndex(i);
                                                                    mainSwiperRef.current?.slideToLoop(i);
                                                                }}
                                                            />
                                                        </SwiperSlide>
                                                    ))}
                                                </Swiper>
                                                <div className="thumb-prev" style={{
                                                    position: "absolute", top: "2px", left: "50%",
                                                    transform: "translateX(-50%)", cursor: "pointer",
                                                    background: "#CEB256", color: "#fff", borderRadius: "50%",
                                                    width: "26px", height: "26px", display: "flex",
                                                    alignItems: "center", justifyContent: "center", zIndex: 20,
                                                    fontSize: "14px",
                                                }}>↑</div>
                                                <div className="thumb-next" style={{
                                                    position: "absolute", bottom: "2px", left: "50%",
                                                    transform: "translateX(-50%)", cursor: "pointer",
                                                    background: "#CEB256", color: "#fff", borderRadius: "50%",
                                                    width: "26px", height: "26px", display: "flex",
                                                    alignItems: "center", justifyContent: "center", zIndex: 20,
                                                    fontSize: "14px",
                                                }}>↓</div>
                                            </div>
                                        )}

                                        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                                            <div
                                                className="pd-main-image-outer"
                                                style={{ height: mainImgHeight }}
                                            >
                                                <Swiper
                                                    className="pd-main-image-swiper"
                                                    loop={imagesToShow.length > 1}
                                                    navigation={{ nextEl: ".main-next", prevEl: ".main-prev" }}
                                                    modules={[Navigation, Thumbs]}
                                                    onSlideChange={(swiper) => {
                                                        setZoomVisible(false);
                                                        setActiveIndex(swiper.realIndex);
                                                    }}
                                                    onSwiper={(swiper) => { mainSwiperRef.current = swiper; }}
                                                    style={{ width: "100%", height: isMobile ? "auto" : "100%" }}
                                                >
                                                    {imagesToShow.map((imgSrc, i) => (
                                                        <SwiperSlide key={i}>
                                                            <div
                                                                style={{ width: "100%", height: "100%", position: "relative" }}
                                                                onMouseEnter={() => isDesktop && i === activeIndex && setZoomVisible(true)}
                                                                onMouseLeave={() => setZoomVisible(false)}
                                                                onMouseMove={handleMouseMove}
                                                                ref={imgRef}
                                                            >
                                                                <img
                                                                    src={imgSrc}
                                                                    alt={`Slide ${i + 1}`}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        objectFit: "cover",
                                                                        objectPosition: "center top",
                                                                        borderRadius: "10px",
                                                                        cursor: isDesktop ? "crosshair" : "pointer",
                                                                        display: "block",
                                                                    }}
                                                                />
                                                                {zoomVisible && isDesktop && i === activeIndex && (
                                                                    <div style={{
                                                                        position: "absolute",
                                                                        top: `${zoomLensPos.y}px`,
                                                                        left: `${zoomLensPos.x}px`,
                                                                        width: "300px", height: "300px",
                                                                        backgroundColor: "rgba(255,255,255,0.15)",
                                                                        backgroundImage: `linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)`,
                                                                        backgroundSize: "30px 30px",
                                                                        pointerEvents: "none", zIndex: 10,
                                                                        border: "1px solid rgba(206,178,86,0.4)",
                                                                        borderRadius: "4px",
                                                                    }} />
                                                                )}
                                                            </div>
                                                        </SwiperSlide>
                                                    ))}
                                                </Swiper>

                                                {imagesToShow.length > 1 && (
                                                    <>
                                                        <div className="main-prev" style={arrowStyle("left")}>‹</div>
                                                        <div className="main-next" style={arrowStyle("right")}>›</div>
                                                    </>
                                                )}
                                            </div>

                                            {zoomVisible && isDesktop && (
                                                <div
                                                    className="pd-zoom-preview"
                                                    style={{
                                                        backgroundImage: `url(${imagesToShow[activeIndex]})`,
                                                        backgroundRepeat: "no-repeat",
                                                        backgroundSize: `${500 * zoomScale}px ${600 * zoomScale}px`,
                                                        backgroundPosition: backgroundPos,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {isMobile && (
                                        <div className="pd-thumb-horizontal">
                                            {imagesToShow.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt={`Thumb ${i + 1}`}
                                                    className={i === activeIndex ? "active-thumb" : ""}
                                                    onClick={() => {
                                                        setMainImage(img);
                                                        setActiveIndex(i);
                                                        mainSwiperRef.current?.slideToLoop(i);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col" style={{ paddingTop: isMobile ? "16px" : "0" }}>
                                <div className="product__details--info pd-product-info">
                                    <form action="#" onSubmit={e => e.preventDefault()}>

                                        <h2
                                            className="product__details--info__title mb-15"
                                            style={{ fontSize: isMobile ? "17px" : isTablet ? "20px" : undefined, lineHeight: 1.3 }}
                                        >
                                            {product.name}
                                        </h2>

                                        <div className="product__details--info__price mb-10" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                            <span className="current__price" style={{ fontSize: isMobile ? "18px" : "22px", fontWeight: 700, color: "#CEB256" }}>
                                                ₹{displayedPrice?.toFixed(2)}
                                            </span>
                                            {originalPrice !== displayedPrice && (
                                                <span className="old__price" style={{ textDecoration: "line-through", color: "#999", fontSize: isMobile ? "14px" : "16px" }}>
                                                    ₹{originalPrice?.toFixed(2)}
                                                </span>
                                            )}
                                            {originalPrice !== displayedPrice && (
                                                <span style={{
                                                    background: "#e8f5e9", color: "#2e7d32",
                                                    fontSize: "12px", fontWeight: 700,
                                                    padding: "2px 8px", borderRadius: "4px",
                                                }}>
                                                    {Math.round(((originalPrice - displayedPrice) / originalPrice) * 100)}% OFF
                                                </span>
                                            )}
                                        </div>

                                        <div className="product__details--info__rating d-flex align-items-center mb-15">
                                            <span className="product__items--rating__count--number" style={{ fontSize: isMobile ? "13px" : undefined }}>
                                                ⭐ {product?.averageRating?.toFixed(1) || "0.0"}
                                            </span>
                                        </div>

                                        <p className="product__details--info__desc mb-15" style={{ fontSize: isMobile ? "13px" : "14px", color: "#555", lineHeight: 1.6 }}>
                                            {product.description}
                                        </p>

                                        <div className="product__variant">

                                            <div className="product__variant--list mb-10">
                                                <fieldset className="variant__input--fieldset" style={{ border: "none", padding: 0, margin: 0 }}>
                                                    <legend className="product__variant--title mb-8" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: 600 }}>
                                                        Color :{" "}
                                                        <span style={{ fontWeight: 700, color: '#333', textTransform: 'capitalize' }}>
                                                            {selectedColor}
                                                        </span>
                                                    </legend>
                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                                        {product?.variants?.map((variant, index) => (
                                                            <React.Fragment key={index}>
                                                                <input
                                                                    id={`color-${index}`}
                                                                    name="color"
                                                                    type="radio"
                                                                    checked={selectedVariantIndex === index}
                                                                    onChange={() => handleColorChange(index)}
                                                                    style={{ display: "none" }}
                                                                />
                                                                <label
                                                                    htmlFor={`color-${index}`}
                                                                    title={variant.color?.name}
                                                                    style={{
                                                                        width: "32px", height: "32px",
                                                                        borderRadius: "50%",
                                                                        cursor: "pointer",
                                                                        display: "inline-flex",
                                                                        alignItems: "center", justifyContent: "center",
                                                                        background: variant.color?.value || "#FFF",
                                                                        border: selectedVariantIndex === index ? "3px solid #CEB256" : "1px solid #ddd",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        outline: selectedVariantIndex === index ? "2px solid #333" : "none",
                                                                        outlineOffset: "1px",
                                                                    }}
                                                                >
                                                                    {selectedVariantIndex === index && (
                                                                        <FaCheckCircle style={{ fontSize: "15px", color: variant.color?.value === '#ffffff' ? '#000' : '#fff' }} />
                                                                    )}
                                                                </label>
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                </fieldset>
                                            </div>

                                            <div className="product__variant--list mb-15">
                                                <fieldset className="variant__input--fieldset weight" style={{ border: "none", padding: 0, margin: 0 }}>
                                                    <legend className="product__variant--title mb-8" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: 600 }}>
                                                        Size :
                                                    </legend>
                                                    {selectedVariant?.sizes?.length > 0 ? (
                                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                                            {selectedVariant.sizes.map((item, index) => (
                                                                <div key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                                    <input
                                                                        id={`size-${index}`}
                                                                        type="radio"
                                                                        name="size"
                                                                        checked={selectedSize === item.size}
                                                                        disabled={item.stock === 0}
                                                                        onChange={() => handleSizeChange(item.size)}
                                                                        style={{ display: "none" }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`size-${index}`}
                                                                        style={{
                                                                            padding: isMobile ? "6px 10px" : "7px 13px",
                                                                            borderRadius: "6px",
                                                                            border: selectedSize === item.size ? "2px solid #CEB256" : "1px solid #ccc",
                                                                            backgroundColor: item.stock === 0 ? "#f5f5f5" : selectedSize === item.size ? "#fff8dc" : "#fff",
                                                                            cursor: item.stock === 0 ? "not-allowed" : "pointer",
                                                                            opacity: item.stock === 0 ? 0.6 : 1,
                                                                            minWidth: "44px",
                                                                            textAlign: "center",
                                                                            fontWeight: 600,
                                                                            fontSize: isMobile ? "12px" : "13px",
                                                                            transition: "all 0.2s ease-in-out",
                                                                            position: "relative",
                                                                        }}
                                                                    >
                                                                        {item.size}
                                                                        {item.stock === 0 && (
                                                                            <span style={{
                                                                                position: "absolute", top: "50%",
                                                                                left: 0, right: 0, height: "1px",
                                                                                background: "#f00",
                                                                                transform: "rotate(-20deg)",
                                                                                pointerEvents: "none",
                                                                            }} />
                                                                        )}
                                                                    </label>
                                                                    {item.stock === 0 && (
                                                                        <span style={{ color: "red", fontSize: "10px", marginTop: "3px" }}>Out of Stock</span>
                                                                    )}
                                                                    {item.stock > 0 && item.stock <= 5 && (
                                                                        <span style={{ color: "#f97316", fontSize: "10px", marginTop: "3px" }}>Only {item.stock} left!</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p style={{ fontSize: "13px", color: "#888" }}>No sizes available</p>
                                                    )}
                                                </fieldset>
                                            </div>

                                            {selectedStock === 0 && (
                                                <div style={{
                                                    background: '#fef2f2', border: '1px solid #fca5a5',
                                                    borderRadius: '6px', padding: '8px 12px',
                                                    marginBottom: '12px', color: '#dc2626',
                                                    fontSize: '13px', fontWeight: 600,
                                                }}>
                                                    ❌ This size is out of stock. Please select another size.
                                                </div>
                                            )}

                                            <div className="product__variant--list quantity mb-20">
                                                <div className="pd-action-row">

                                                    {isInCart ? (
                                                        <button
                                                            type="button"
                                                            className="pd-addcart-btn"
                                                            disabled={selectedStock === 0}
                                                            onClick={async () => {
                                                                navigate("/add-to-cart");
                                                            }}
                                                        >
                                                            <FaShoppingCart />
                                                            View Cart
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="pd-addcart-btn"
                                                            disabled={selectedStock === 0}
                                                            onClick={async () => {
                                                                try {
                                                                    addToCart(product, selectedColor, selectedSize, quantity);
                                                                    toast.success(`${quantity} item(s) added to cart!`, {
                                                                        position: "top-right", autoClose: 3000, theme: "dark",
                                                                    });
                                                                } catch {
                                                                    toast.error("This variant is out of stock", {
                                                                        position: "top-right", autoClose: 3000, theme: "dark",
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <FaShoppingCart />
                                                            {selectedStock === 0 ? "Out of Stock" : "Add to Cart"}
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className="pd-buynow-btn"
                                                        onClick={() => handleBuyNow(product)}
                                                        disabled={selectedStock === 0}
                                                        style={{ background: selectedStock === 0 ? "#ccc" : "#28a745" }}
                                                    >
                                                        Buy Now
                                                    </button>
                                                </div>
                                            </div>

                                            {availableCoupon && (
                                                <div className="pd-coupon-box">
                                                    <p style={{ fontSize: isMobile ? "12px" : "13px", margin: "0 0 6px" }}>
                                                        Get Extra{" "}
                                                        <span style={{ color: "green", fontWeight: 700 }}>
                                                            {availableCoupon.discountType === "percentage"
                                                                ? `${availableCoupon.discountValue}%`
                                                                : `₹${availableCoupon.discountValue}`}{" "}Discount
                                                        </span>{" "}
                                                        on orders above ₹{availableCoupon.minOrderAmount}
                                                    </p>
                                                    <div style={{
                                                        display: "flex", justifyContent: "space-between",
                                                        alignItems: "center", gap: "8px",
                                                        border: "1px dashed #CEB256", borderRadius: "4px",
                                                        padding: "6px 10px", background: "#f9fff9",
                                                    }}>
                                                        <span style={{ fontSize: "16px", fontWeight: 700, color: "#CEB256", letterSpacing: "2px" }}>
                                                            {availableCoupon.code}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            style={{
                                                                background: "#CEB256", color: "#fff",
                                                                fontWeight: 600, border: "none",
                                                                borderRadius: "4px", padding: "5px 12px",
                                                                cursor: "pointer", fontSize: "13px",
                                                                flexShrink: 0,
                                                            }}
                                                            onClick={() => handleCopy()}
                                                        >
                                                            {copied ? "✅ Copied!" : "Copy"}
                                                        </button>
                                                    </div>
                                                    <p style={{ fontSize: "10px", color: "#888", margin: "4px 0 0" }}>
                                                        *Terms & Conditions apply. Limited period offer.
                                                    </p>
                                                </div>
                                            )}

                                            <div style={{ marginTop: "14px" }}>
                                                <span style={{ fontSize: isMobile ? "13px" : "15px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <FaMapMarkerAlt style={{ color: "#CEB256" }} />
                                                    Check Delivery Details
                                                </span>
                                                <div className="pd-pincode-wrap">
                                                    <input
                                                        type="text"
                                                        value={pincode}
                                                        placeholder="Enter 6-digit Pincode"
                                                        onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => pincodeCheck()}
                                                    >
                                                        Check
                                                    </button>
                                                </div>
                                                {message && (
                                                    <p style={{
                                                        marginTop: "8px", fontSize: "13px",
                                                        display: "flex", alignItems: "center", gap: "6px",
                                                        color: message.includes("unable") ? "red" : "#333",
                                                        flexWrap: "wrap",
                                                    }}>
                                                        {message.includes("unable")
                                                            ? <FaTimesCircle style={{ color: 'red', flexShrink: 0 }} />
                                                            : <FaTruck style={{ color: "#CEB256", flexShrink: 0 }} />
                                                        }
                                                        {message.includes("Expected delivery by") ? (
                                                            <>
                                                                <span>Expected delivery by </span>
                                                                <span style={{ color: "green", fontWeight: 600 }}>
                                                                    {message.split("Expected delivery by")[1]}
                                                                </span>
                                                            </>
                                                        ) : message}
                                                    </p>
                                                )}
                                            </div>

                                        </div>
                                    </form>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                <section className="product__details--tab__section section--padding">
                    <div className="container">
                        <div className="row row-cols-1">
                            <div className="col">
                                <ul className="product__details--tab">
                                    {[
                                        ["description", "Specifications"],
                                        ["reviews", "Material & Care"],
                                        ["information", "Rating & Reviews"],
                                    ].map(([key, label]) => (
                                        <li
                                            key={key}
                                            className={`product__details--tab__list ${activeTab === key ? "active" : ""}`}
                                            onClick={() => setActiveTab(key)}
                                        >
                                            {label}
                                        </li>
                                    ))}
                                </ul>
                                <div className="product__details--tab__content" style={{ fontSize: isMobile ? "13px" : "14px", color: "#444", lineHeight: 1.7, padding: "4px 0" }}>
                                    {activeTab === "description" && <p>{product.specifications}</p>}
                                    {activeTab === "reviews" && <p>{product.materialCare}</p>}
                                    {activeTab === "information" && <p>{product.numOfReviews} reviews</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="product__section section--padding pt-0">
                    <div className="container">
                        <div className="section__heading text-center mb-50">
                            <h2 className="section__heading--maintitle" style={{ fontSize: isMobile ? "18px" : undefined }}>
                                You may also like
                            </h2>
                        </div>
                        <div className="row g-3 justify-content-center">
                            {refference?.length > 0 ? refference.map((reff) => {
                                const firstVariant = reff.firstVariant;
                                const discountedPrice = firstVariant?.discountedPrice ?? 0;
                                const origPrice = firstVariant?.originalPrice ?? 0;
                                const image = firstVariant?.image || firstVariant?.images?.[0] || "https://via.placeholder.com/150";

                                const stock = reff.variants?.every((variant) =>
                                    variant.sizes?.every((size) => size.stock === 0)
                                );

                                return (
                                    <div key={reff._id} className="col-6 col-sm-6 col-md-4 col-lg-3 d-flex justify-content-center">
                                        <div className="pd-ref-card" style={{ cursor: "pointer" }} onClick={() => goToSingle(reff)}>
                                            <img
                                                src={image}
                                                alt={reff.name}
                                                style={{
                                                    width: "100%",
                                                    height: isMobile ? "130px" : "170px",
                                                    objectFit: "cover",
                                                    objectPosition: "center top",
                                                    borderRadius: "6px",
                                                    display: "block",
                                                }}
                                            />
                                            <h4 style={{
                                                fontSize: isMobile ? "12px" : "15px",
                                                margin: "8px 0 4px",
                                                lineHeight: "1.3",
                                                color: "#222",
                                            }}>
                                                {reff.name}
                                            </h4>
                                            <p style={{ fontSize: isMobile ? "12px" : "14px", color: "#333", margin: "0 0 4px" }}>
                                                <span style={{ fontWeight: 700 }}>₹{discountedPrice}</span>
                                                {origPrice !== discountedPrice && (
                                                    <span style={{ textDecoration: "line-through", marginLeft: "6px", color: "#999" }}>
                                                        ₹{origPrice}
                                                    </span>
                                                )}
                                            </p>
                                            {stock == true && (
                                                <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600 }}>Out of Stock</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            }) : (
                                <p style={{ textAlign: "center", color: "#888" }}>No related products found.</p>
                            )}
                        </div>
                    </div>
                </section>

            </main>

            <Footer />

            {isVisible && (
                <button
                    onClick={handleScrollTop}
                    aria-label="Scroll to top"
                    style={{
                        position: 'fixed', bottom: '20px', right: '20px',
                        width: isMobile ? "40px" : "48px",
                        height: isMobile ? "40px" : "48px",
                        borderRadius: '50%',
                        backgroundColor: '#CEB256', color: '#fff',
                        border: 'none', zIndex: 1000, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
                        transition: 'opacity 0.3s',
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20"
                        viewBox="0 0 512 512" fill="none" stroke="currentColor"
                        strokeWidth="52" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M112 244l144-144 144 144" />
                        <path d="M256 120v292" />
                    </svg>
                </button>
            )}
        </>
    );
};

export default ProductDetails;